// Checkout.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Checkout.css';

function Checkout() {
	const navigate = useNavigate();
	const [orderItems, setOrderItems] = useState([]);
	const [isDirectOrder, setIsDirectOrder] = useState(false);
	const [formData, setFormData] = useState({
		name: '',
		phone: '',
		postal_code: '',
		address: '',
		address_detail: '',
		customs_number: '',
		memo: ''
	});
	const [profileLoaded, setProfileLoaded] = useState(false);
	const [loading, setLoading] = useState(true);
	const hasLoadedRef = useRef(false);
	
	// 쿠폰/포인트 상태
	const [coupons, setCoupons] = useState([]);
	const [selectedCoupon, setSelectedCoupon] = useState(null);
	const [pointBalance, setPointBalance] = useState(0);
	const [usePoint, setUsePoint] = useState(0);
	const [showCouponModal, setShowCouponModal] = useState(false);

	const loadOrderItems = () => {
		console.log('📦 주문 상품 로딩 시작...');
		
		const directOrder = localStorage.getItem('directOrder');
		console.log('🔍 directOrder 확인:', directOrder);
		
		if (directOrder) {
			try {
				const items = JSON.parse(directOrder);
				console.log('💳 바로구매 데이터 파싱:', items);
				
				if (items && Array.isArray(items) && items.length > 0) {
					console.log('✅ 바로구매 데이터 사용');
					setOrderItems(items);
					setIsDirectOrder(true);
					localStorage.removeItem('directOrder');
					return;
				}
			} catch (error) {
				console.error('❌ 바로구매 데이터 파싱 실패:', error);
			}
		}
		
		const checkoutItems = localStorage.getItem('checkoutItems');
		console.log('🔍 checkoutItems 확인:', checkoutItems);
		
		if (checkoutItems) {
			try {
				const items = JSON.parse(checkoutItems);
				console.log('🛒 장바구니 선택 상품 파싱:', items);
				
				if (items && Array.isArray(items) && items.length > 0) {
					console.log('✅ 장바구니 선택 상품 사용');
					setOrderItems(items);
					setIsDirectOrder(false);
					localStorage.removeItem('checkoutItems');
					return;
				}
			} catch (error) {
				console.error('❌ 장바구니 선택 상품 파싱 실패:', error);
			}
		}
		
		console.log('⚠️ 주문할 상품 없음');
		alert('주문할 상품이 없습니다.');
		navigate('/cart');
	};

	const fetchProfile = async () => {
		const token = localStorage.getItem('access_token');
		
		if (!token) {
			setLoading(false);
			return;
		}

		try {
			console.log('👤 프로필 정보 로딩...');
			
			const response = await axios.get(
				'https://api.weartoday.kr/api/users/profile/',
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);
			
			console.log('✅ 프로필 응답:', response.data);
			
			const profile = response.data;
			
			if (!profile.customs_number) {
				console.log('⚠️ 개인통관번호 없음');
				
				if (window.confirm(
					'주문을 위해 개인통관고유부호가 필요합니다.\n\n' +
					'회원정보 페이지로 이동하시겠습니까?'
				)) {
					navigate('/profile');
					return;
				} else {
					navigate('/');
					return;
				}
			}
			
			setFormData({
				name: profile.name || '',
				phone: profile.phone || '',
				postal_code: profile.postal_code || '',
				address: profile.address || '',
				address_detail: profile.address_detail || '',
				customs_number: profile.customs_number || '',
				memo: ''
			});
			
			setProfileLoaded(true);
			console.log('✅ 프로필 정보 로딩 완료');
			
		} catch (error) {
			console.error('❌ 프로필 로딩 실패:', error);
			
			if (error.response?.status === 401) {
				alert('로그인이 만료되었습니다.');
				navigate('/login');
			} else {
				console.log('⚠️ 프로필 없이 진행');
			}
		} finally {
			setLoading(false);
		}
	};

	const fetchCouponsAndPoints = async () => {
		const token = localStorage.getItem('access_token');
		if (!token) return;

		try {
			// 쿠폰 조회
			const couponRes = await axios.get(
				'https://api.weartoday.kr/api/orders/coupons/',
				{ headers: { 'Authorization': `Bearer ${token}` } }
			);
			const availableCoupons = (couponRes.data.results || couponRes.data || [])
				.filter(c => !c.is_used && new Date(c.expired_at) > new Date());
			setCoupons(availableCoupons);
			console.log('✅ 쿠폰 로드:', availableCoupons.length, '개');

			// 포인트 잔액 조회
			const pointRes = await axios.get(
				'https://api.weartoday.kr/api/orders/points/balance/',
				{ headers: { 'Authorization': `Bearer ${token}` } }
			);
			setPointBalance(pointRes.data.balance || 0);
			console.log('✅ 포인트 잔액:', pointRes.data.balance);

		} catch (error) {
			console.error('❌ 쿠폰/포인트 로드 실패:', error);
		}
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		});
	};

	const getTotalPrice = () => {
		return orderItems.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);
	};

	const getCouponDiscount = () => {
		if (!selectedCoupon) return 0;
		
		const totalPrice = getTotalPrice();
		if (totalPrice < selectedCoupon.min_purchase_amount) return 0;

		let discount = 0;
		if (selectedCoupon.discount_type === 'percent') {
			discount = Math.floor(totalPrice * selectedCoupon.discount_value / 100);
			if (selectedCoupon.max_discount_amount) {
				discount = Math.min(discount, selectedCoupon.max_discount_amount);
			}
		} else {
			discount = selectedCoupon.discount_value;
		}
		return discount;
	};

	const getFinalPrice = () => {
		return getTotalPrice() - getCouponDiscount() - usePoint;
	};

	const handlePointChange = (e) => {
		let value = parseInt(e.target.value) || 0;
		const maxUsable = Math.min(pointBalance, getTotalPrice() - getCouponDiscount());
		value = Math.max(0, Math.min(value, maxUsable));
		
		// ⭐ 10,000원 미만이면 0으로
		if (value > 0 && value < 10000) {
			alert('포인트는 10,000P 이상부터 사용 가능합니다.');
			value = 0;
		} else if (value >= 10000) {
			// ⭐ 1만원 단위로 내림
			value = Math.floor(value / 10000) * 10000;
		}
		
		setUsePoint(value);
	};

	const useAllPoints = () => {
		const maxUsable = Math.min(pointBalance, getTotalPrice() - getCouponDiscount());
		
		// ⭐ 10,000원 미만이면 사용 불가
		if (maxUsable < 10000) {
			alert('포인트는 10,000P 이상부터 사용 가능합니다.');
			setUsePoint(0);
			return;
		}
		
		// ⭐ 1만원 단위로 내림
		const useAmount = Math.floor(maxUsable / 10000) * 10000;
		setUsePoint(useAmount);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!formData.name || !formData.phone || !formData.customs_number) {
			alert('이름, 전화번호, 개인통관고유부호는 필수 정보입니다!');
			return;
		}

		if (!formData.postal_code || !formData.address || !formData.address_detail) {
			alert('배송지 정보를 모두 입력해주세요!');
			return;
		}

		const customsRegex = /^P[0-9]{12}$/;
		if (!customsRegex.test(formData.customs_number)) {
			alert('올바른 개인통관고유부호 형식이 아닙니다. (예: P123456789012)');
			return;
		}

		const token = localStorage.getItem('access_token');
		if (!token) {
			alert('로그인이 필요합니다.');
			navigate('/login');
			return;
		}

		try {
			const orderData = {
				items: orderItems.map(item => ({
					product_id: item.product?.id || item.id,
					product_name: item.product?.name || item.name,
					option_name: item.option?.option_name || item.option?.name || item.option_name || '',
					size: item.size || item.option?.size || '',
					quantity: item.quantity,
					price: Number(item.price),
					total_price: Number(item.price) * item.quantity
				})),
				total_amount: getTotalPrice(),
				discount_amount: getCouponDiscount() + usePoint,
				delivery_fee: 0,
				final_amount: getFinalPrice(),
				payment_method: 'pending',
				recipient_name: formData.name,
				recipient_phone: formData.phone,
				postal_code: formData.postal_code,
				address: formData.address,
				address_detail: formData.address_detail,
				delivery_memo: formData.memo,
				status: 'pending',
				coupon_id: selectedCoupon?.id || null,
				used_point: usePoint
			};

			console.log('📤 주문 데이터 전송:', orderData);

			const response = await axios.post(
				'https://api.weartoday.kr/api/orders/',
				orderData,
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);

			console.log('✅ 주문 생성 성공:', response.data);

			if (!isDirectOrder) {
				try {
					const productIds = orderItems.map(item => item.product?.id || item.id);
					
					for (const productId of productIds) {
						const cartItem = orderItems.find(item => 
							(item.product?.id || item.id) === productId
						);
						
						if (cartItem && cartItem.id) {
							await axios.delete(
								`https://api.weartoday.kr/api/products/cart/${cartItem.id}/`,
								{
									headers: {
										'Authorization': `Bearer ${token}`
									}
								}
							);
							console.log(`✅ 장바구니 삭제 성공: ${cartItem.id}`);
						}
					}
					
					localStorage.removeItem('checkoutItems');
					window.dispatchEvent(new Event('cartUpdated'));
					
				} catch (deleteError) {
					console.error('❌ 장바구니 삭제 실패:', deleteError);
				}
				}

				// ⭐ navigate 전에 이벤트 발생
				window.dispatchEvent(new Event('cartUpdated'));

				alert('주문이 완료되었습니다!');
				navigate('/mypage');

		} catch (error) {
			console.error('❌ 주문 실패:', error);
			console.error('❌ 응답 데이터:', error.response?.data);
			alert(`주문 실패: ${JSON.stringify(error.response?.data || '알 수 없는 오류')}`);
		}
	};

	useEffect(() => {
		if (hasLoadedRef.current) {
			console.log('⚠️ 이미 로딩됨, 스킵');
			return;
		}
		
		console.log('🎯 Checkout 마운트');
		hasLoadedRef.current = true;
		
		const token = localStorage.getItem('access_token');
		if (!token) {
			alert('로그인이 필요합니다.');
			navigate('/login');
			return;
		}
		
		loadOrderItems();
		fetchProfile();
		fetchCouponsAndPoints();
	}, [navigate]);

	if (loading) {
		return (
			<div className="checkout-page">
				<div className="loading">
					⏳ 주문 정보를 불러오는 중...
				</div>
			</div>
		);
	}

	if (orderItems.length === 0) {
		return (
			<div className="checkout-page">
				<div className="empty-cart">
					<h2>주문할 상품이 없습니다</h2>
					<button onClick={() => navigate('/')}>쇼핑 계속하기</button>
				</div>
			</div>
		);
	}

	return (
		<div className="checkout-page">
			<h1>주문/결제</h1>
			
			<div className="checkout-container">
				<div className="order-items">
					<h2>주문 상품</h2>
					{orderItems.map((item, index) => {
						let imageUrl = item.image || 
									   item.product?.image || 
									   item.product?.main_images?.[0] ||
									   'https://via.placeholder.com/120?text=No+Image';
						
						if (imageUrl && imageUrl.startsWith('/media')) {
							imageUrl = `https://api.weartoday.kr${imageUrl}`;
						}
						
						const optionName = item.option_name || item.option?.option_name || item.option?.name || '';
						const sizeName = item.size || item.option?.size || '';
						
						return (
							<div key={`${item.id}-${index}`} className="order-item">
								<img 
									src={imageUrl} 
									alt={item.name || item.product?.name}
								/>
								<div className="item-info">
									<h3>{item.name || item.product?.name}</h3>
									{(optionName || sizeName) && (
										<p>
											{optionName && `옵션: ${optionName}`}
											{optionName && sizeName && '  /  '}
											{sizeName && `사이즈: ${sizeName}`}
										</p>
									)}
									<p>{Number(item.price).toLocaleString()}원 × {item.quantity}개</p>
								</div>
								<div className="item-total">
									{(Number(item.price) * item.quantity).toLocaleString()}원
								</div>
							</div>
						);
					})}
					
					<div className="order-items-total">
						<span>주문 상품 합계</span>
						<span>{getTotalPrice().toLocaleString()}원</span>
					</div>
				</div>

				<form className="order-form" onSubmit={handleSubmit}>
					<h2>배송 정보</h2>
					
					{profileLoaded && (
						<div className="profile-loaded-notice">
							✅ 회원 정보에서 자동으로 불러왔습니다.
						</div>
					)}
					
					<div className="form-group">
						<label>이름 <span>*</span></label>
						<input
							type="text"
							name="name"
							value={formData.name}
							onChange={handleChange}
							placeholder="받는 분 이름"
							required
							readOnly={profileLoaded}
						/>
					</div>

					<div className="form-group">
						<label>연락처 <span>*</span></label>
						<input
							type="tel"
							name="phone"
							value={formData.phone}
							onChange={handleChange}
							placeholder="010-1234-5678"
							required
							readOnly={profileLoaded}
						/>
					</div>

					<div className="form-group">
						<label>개인통관고유부호 <span>*</span></label>
						<input
							type="text"
							name="customs_number"
							value={formData.customs_number}
							onChange={handleChange}
							placeholder="P123456789012"
							maxLength="13"
							required
							readOnly={profileLoaded}
						/>
						<div className="customs-warning">
							⚠️ 개인통관고유부호가 부정확하면 세관에서 통관이 늦어지고 분실위험 있습니다!!
						</div>
					</div>

					<div className="form-group">
						<label>우편번호 <span>*</span></label>
						<div className="address-search-wrapper">
							<input
								type="text"
								name="postal_code"
								value={formData.postal_code}
								readOnly
								placeholder="우편번호"
								required
							/>
							<button 
								type="button"
								className="address-search-btn"
								onClick={() => {
									new window.daum.Postcode({
										oncomplete: function(data) {
											setFormData(prev => ({
												...prev,
												postal_code: data.zonecode,
												address: data.address
											}));
										}
									}).open();
								}}
							>
								주소 검색
							</button>
						</div>
					</div>

					<div className="form-group">
						<label>기본주소 <span>*</span></label>
						<input
							type="text"
							name="address"
							value={formData.address}
							readOnly
							placeholder="주소 검색 버튼을 클릭하세요"
							required
						/>
					</div>

					<div className="form-group">
						<label>상세주소 <span>*</span></label>
						<input
							type="text"
							name="address_detail"
							value={formData.address_detail}
							onChange={handleChange}
							placeholder="상세주소를 입력하세요"
							required
						/>
					</div>

					<div className="form-group">
						<label>배송 메모</label>
						<input
							type="text"
							name="memo"
							value={formData.memo}
							onChange={handleChange}
							placeholder="배송 시 요청사항 (선택, 100자 이내)"
							maxLength="100"
						/>
					</div>

					<div className="order-summary">
						<div className="summary-row">
							<span>상품 금액</span>
							<span>{getTotalPrice().toLocaleString()}원</span>
						</div>
						
						{/* 쿠폰 */}
						<div className="summary-row coupon-row">
							<span>쿠폰 할인</span>
							<div className="coupon-select">
								{selectedCoupon ? (
									<div className="selected-coupon">
										<span className="coupon-name">{selectedCoupon.name}</span>
										<span className="coupon-discount">-{getCouponDiscount().toLocaleString()}원</span>
										<button type="button" className="coupon-cancel" onClick={() => setSelectedCoupon(null)}>✕</button>
									</div>
								) : (
									<button type="button" className="coupon-btn" onClick={() => setShowCouponModal(true)}>
										쿠폰 선택 ({coupons.length}장)
									</button>
								)}
							</div>
						</div>

						{/* 포인트 */}
						<div className="summary-row point-row">
							<span>포인트 사용</span>
							<div className="point-input-wrapper">
								<input
									type="number"
									value={usePoint}
									onChange={handlePointChange}
									min="0"
									max={Math.min(pointBalance, getTotalPrice() - getCouponDiscount())}
								/>
								<span className="point-unit">P</span>
								<button type="button" className="point-all-btn" onClick={useAllPoints}>전액사용</button>
								<span className="point-balance">보유: {pointBalance.toLocaleString()}P</span>
							</div>
						</div>

						<div className="summary-row">
							<span>배송비</span>
							<span>무료</span>
						</div>
						
						{(getCouponDiscount() > 0 || usePoint > 0) && (
							<div className="summary-row discount-total">
								<span>총 할인</span>
								<span className="discount-amount">-{(getCouponDiscount() + usePoint).toLocaleString()}원</span>
							</div>
						)}
						
						<div className="summary-row total">
							<span>최종 결제 금액</span>
							<span>{getFinalPrice().toLocaleString()}원</span>
						</div>
					</div>

					<button type="submit" className="submit-button">
						{getFinalPrice().toLocaleString()}원 결제하기
					</button>
				</form>
			</div>

			{/* 쿠폰 선택 모달 */}
			{showCouponModal && (
				<div className="coupon-modal-overlay" onClick={() => setShowCouponModal(false)}>
					<div className="coupon-modal" onClick={e => e.stopPropagation()}>
						<div className="coupon-modal-header">
							<h3>쿠폰 선택</h3>
							<button className="modal-close" onClick={() => setShowCouponModal(false)}>✕</button>
						</div>
						<div className="coupon-modal-body">
							{coupons.length === 0 ? (
								<div className="no-coupon">사용 가능한 쿠폰이 없습니다.</div>
							) : (
								coupons.map(coupon => {
									const isUsable = getTotalPrice() >= coupon.min_purchase_amount;
									return (
										<div 
											key={coupon.id} 
											className={`coupon-item ${!isUsable ? 'disabled' : ''} ${selectedCoupon?.id === coupon.id ? 'selected' : ''}`}
											onClick={() => {
												if (isUsable) {
													setSelectedCoupon(coupon);
													setShowCouponModal(false);
												}
											}}
										>
											<div className="coupon-item-left">
												<div className="coupon-value">
													{coupon.discount_type === 'percent' 
														? `${coupon.discount_value}%` 
														: `${coupon.discount_value.toLocaleString()}원`}
												</div>
											</div>
											<div className="coupon-item-right">
												<div className="coupon-item-name">{coupon.name}</div>
												<div className="coupon-item-condition">
													{coupon.min_purchase_amount > 0 
														? `${coupon.min_purchase_amount.toLocaleString()}원 이상 구매 시` 
														: '조건 없음'}
												</div>
												<div className="coupon-item-expire">
													{new Date(coupon.expired_at).toLocaleDateString('ko-KR')} 까지
												</div>
												{!isUsable && (
													<div className="coupon-item-warning">최소 주문금액 미달</div>
												)}
											</div>
										</div>
									);
								})
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default Checkout;